import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import {
  NgSignaturePadOptions,
  SignaturePadComponent as SignaturePadComponentAlias,
} from '@almothafar/angular-signature-pad';
import {
  MatDialogClose,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { EjournalApiService } from '@et/api';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import {
  EtJournalSigner,
  PatchEjournalSignerSignatureDto,
  AddEjournalSignerSignatureDto,
  EjournalSignerSignature,
} from '@et/typings';
import { finalize, catchError } from 'rxjs';
import { EjournalService } from '../../services/ejournal.service';

@Component({
  selector: 'et-journal-signature-pad',
  imports: [
    SignaturePadComponentAlias,
    MatDialogClose,
    PresentationalAtomsModule,
  ],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignaturePadComponent implements AfterViewInit {
  private dialogRef = inject(MatDialogRef<SignaturePadComponent, string>);
  private signer = inject<EtJournalSigner>(MAT_DIALOG_DATA);
  private ejournalService = inject(EjournalService);
  private ejournalApiService = inject(EjournalApiService);

  error = signal<string | null>(null);
  isLoading = signal(false);

  signaturePadOptions: NgSignaturePadOptions = {
    minWidth: 2,
    maxWidth: 4,
    dotSize: 2,
    canvasWidth: 650,
    canvasHeight: 200,
    backgroundColor: 'rgba(0, 0, 0, 0)',
  };

  get isEditing() {
    return this.signer.signatures.length > 0;
  }

  @ViewChild(SignaturePadComponentAlias)
  signaturePad!: SignaturePadComponentAlias;

  ngAfterViewInit(): void {
    const signature = this.signer.signatures[0];
    if (signature && signature.signatureImg) {
      this.signaturePad.fromDataURL(signature.signatureImg);
    }
  }

  /**
   * Persists the canvas as PNG: creates a new signature or updates the existing one, then closes the dialog with the result.
   */
  onSave() {
    const isEmpty = this.signaturePad.isEmpty();
    if (isEmpty) {
      this.error.set('Signature is required');
      return;
    }
    const img = this.signaturePad.toDataURL('image/png');

    if (this.isEditing) {
      this.updateSignature(img);
    } else {
      this.addSignature(img);
    }
  }

  /**
   * Clears the pad and any validation error message.
   */
  onClear() {
    this.signaturePad.clear();
    this.error.set(null);
  }

  /**
   * Patches the first signature for this signer and syncs local state.
   *
   * @param img - PNG data URL from the signature pad.
   */
  private updateSignature(img: string) {
    const packet = this.ejournalService.selectedPacket()!;
    const signature = this.signer.signatures[0];
    const patchEjournalSignerSignatureDto: PatchEjournalSignerSignatureDto = {
      id: signature.id,
      packetId: packet.packetId!,
      eJournalSignerId: this.signer.eJournalSignerId,
      signatureImg: img,
    };

    this.isLoading.set(true);
    this.ejournalApiService
      .updateSignerSignature(signature.id, patchEjournalSignerSignatureDto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        catchError((error) => {
          this.error.set('Failed to update signature');
          throw error;
        }),
      )
      .subscribe((signature) => {
        this.addSignerSignature(signature);
        this.dialogRef.close(signature);
      });
  }

  /**
   * Creates a new signature for this signer and syncs local state.
   *
   * @param signatureImg - PNG data URL from the signature pad.
   */
  private addSignature(signatureImg: string) {
    const packet = this.ejournalService.selectedPacket()!;
    const addEjournalSignerSignatureDto: AddEjournalSignerSignatureDto = {
      packetId: packet.packetId!,
      eJournalSignerId: this.signer.eJournalSignerId,
      eJournalEntryIds: [],
      signatureImg,
    };

    this.isLoading.set(true);
    this.ejournalApiService
      .addSignerSignature(addEjournalSignerSignatureDto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        catchError((error) => {
          this.error.set('Failed to add signature');
          throw error;
        }),
      )
      .subscribe((signature) => {
        this.addSignerSignature(signature);
        this.dialogRef.close(signature);
      });
  }

  /**
   * Replaces the matching signer's `signatures` array with the saved signature in the shared add-ejournal service state.
   *
   * @param signature - Signature returned from the API.
   */
  private addSignerSignature(signature: EjournalSignerSignature) {
    this.ejournalService.updateSigners((signers) => {
      const signerIndex = signers.findIndex(
        (signer) => signer.eJournalSignerId === this.signer.eJournalSignerId,
      );
      if (signerIndex !== -1) {
        const copySigner = {
          ...signers[signerIndex],
          signatures: [signature],
        };
        signers[signerIndex] = copySigner;
      }
      return signers;
    });
  }
}
